// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RequestState } from '../common'
import { BreadcrumbSegment } from '../../components/BreadcrumbNavigation'
import { deviceUpgradeMonitoring, siteMonitoringPackageVersions, siteUpgradeMonitoring } from './thunks'
import { SelectedSite, SiteMonitoring } from '../../models/upgradeMonitoring/upgradeMonitoring'
import { sortedProductShortName } from '../../common/constants/supportedProducts'
import { PackageVersionsResponse, ProductVersion } from '../../models/upgradeMonitoring/packageVersions'
import { Option } from '../../components/Dropdown'
import { DeviceMonitoring } from '../../models/upgradeMonitoring/deviceMonitoring'
import { COMPANY_CHANGE, LOGOUT } from '../globalActions'

export interface UpgradeMonitoringState {
  siteUpgradeMonitoringState: RequestState
  sites: SiteMonitoring[]
  breadcrumbs: BreadcrumbSegment[]
  selectedSite: SelectedSite | undefined
  siteMonitoringPackageVersionsState: RequestState
  packageVersions: PackageVersionsResponse | undefined
  deviceUpgradeMonitoringState: RequestState,
  devices: DeviceMonitoring[]
  refreshRate: Option
}

const initialState: UpgradeMonitoringState = {
  siteUpgradeMonitoringState: 'idle',
  sites: [],
  breadcrumbs: [],
  selectedSite: undefined,
  siteMonitoringPackageVersionsState: 'idle',
  packageVersions: undefined,
  deviceUpgradeMonitoringState: 'idle',
  devices: [],
  refreshRate: { label: 'None', value: 0 }
}

export const upgradeMonitoringSlice = createSlice({
  name: 'upgradeMonitoring',
  initialState,
  reducers: {
    setSiteUpgradeMonitoringState: (
      state,
      action: PayloadAction<RequestState>
    ) => {
      state.siteUpgradeMonitoringState = action.payload
    },
    setUpgradeMonitoringBreadcrumbs: (state, action: PayloadAction<BreadcrumbSegment[]>) => {
      state.breadcrumbs = [...action.payload].sort((a, b) => a.order - b.order)
    },
    setSelectedSite: (state, action: PayloadAction<SelectedSite | undefined>) => {
      state.selectedSite = action.payload
    },
    setRemovePackageVersions: (state, action: PayloadAction<undefined>) => {
      state.packageVersions = action.payload
    },
    setRefreshRate: (state, action: PayloadAction<Option>) => {
      state.refreshRate = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(siteUpgradeMonitoring.pending, (state) => {
      state.siteUpgradeMonitoringState = 'pending'
    })
    builder.addCase(siteUpgradeMonitoring.rejected, (state) => {
      state.siteUpgradeMonitoringState = 'rejected'
    })
    builder.addCase(siteUpgradeMonitoring.fulfilled, (state, action) => {
      state.sites = action.payload
      state.siteUpgradeMonitoringState = 'fulfilled'
    })
    builder.addCase(siteMonitoringPackageVersions.pending, (state) => {
      state.siteMonitoringPackageVersionsState = 'pending'
    })
    builder.addCase(siteMonitoringPackageVersions.rejected, (state) => {
      // * Note: Set the request state to pending so the site monitoring drawer table continues to spin for the new request.
      state.siteMonitoringPackageVersionsState = 'pending'
    })
    builder.addCase(siteMonitoringPackageVersions.fulfilled, (state, action) => {
      const { packageVersions } = action.payload
      const sorted = sortedProductShortName.map((p) => packageVersions?.find((v) => v.product === p)).filter((p): p is ProductVersion => !!p)
      state.packageVersions = {
        ...action.payload,
        packageVersions: sorted
      }
      state.siteMonitoringPackageVersionsState = 'fulfilled'
    })
    builder.addCase(deviceUpgradeMonitoring.pending, (state) => {
      state.deviceUpgradeMonitoringState = 'pending'
    })
    builder.addCase(deviceUpgradeMonitoring.rejected, (state) => {
      state.deviceUpgradeMonitoringState = 'rejected'
    })
    builder.addCase(deviceUpgradeMonitoring.fulfilled, (state, action) => {
      state.devices = action.payload
      state.deviceUpgradeMonitoringState = 'fulfilled'
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
    builder.addCase(COMPANY_CHANGE, () => initialState)
  }
})

export default upgradeMonitoringSlice.reducer
export const {
  setSiteUpgradeMonitoringState,
  setUpgradeMonitoringBreadcrumbs,
  setSelectedSite,
  setRemovePackageVersions,
  setRefreshRate
} = upgradeMonitoringSlice.actions
